// articles/PuttsGame.js
// Game guide (Oct 4, 2026), in the Nassau guide's format. Written from the live code: the
// putts card in Games Setup (App.js — "per putt differential"), the putts entry on the hole
// screen and the engine (gameEngines/putts.js: holes with no putts entered don't count).
import React from 'react';
import { colors, getGuideBySlug } from '../guidesData';
import { H2, H3, P, UL, LI, GuideCTA, FAQ } from '../articleUI';
import { Step, Btn, Heads, StepTracker, PRINT_CSS } from './eventGuideParts';

const guide = getGuideBySlug('how-to-set-up-a-putts-golf-game');

const FAQS = [
  {
    q: 'What if a player forgets to enter his putts on a hole?',
    a: 'A hole with no putts entered isn\u2019t counted, which helps that player. Check the cards before the round is final, and fill in any missing putts in Admin → ⛳ Scoring → ⛳ Post-Round Score Entry.',
  },
  {
    q: 'How do I enter a chip-in?',
    a: 'Tap the putts box once: it shows 0 in grey until you tap it, then a dark 0. A dark 0 counts as zero putts; a grey 0 means nothing has been entered.',
  },
  {
    q: 'Do handicaps matter?',
    a: 'No. Putts Differential has no strokes; fewer putts wins.',
  },
  {
    q: 'Can we play it on nine holes?',
    a: 'Yes. On a nine-hole round it counts the putts on those nine holes.',
  },
  {
    q: 'What if two players have the same number of putts?',
    a: 'That pair is a push and no money changes hands between them.',
  },
];

export default function PuttsGame() {
  return (
    <article>
      <h1
        style={{
          fontFamily: "'DM Serif Display', Georgia, serif",
          fontSize: 38,
          fontWeight: 400,
          color: colors.gray900,
          margin: '0 0 8px',
          lineHeight: 1.15,
        }}
      >
        How to Set Up Putts Differential
      </h1>
      <style>{PRINT_CSS}</style>
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', margin: '0 0 28px' }}
      >
        <p style={{ fontSize: 15, color: colors.gray400, margin: 0 }}>
          {guide.readMinutes} min read &middot; Game guide
        </p>
        <button
          type="button"
          className="guide-no-print"
          onClick={() => window.print()}
          style={{
            background: 'white',
            color: colors.green800,
            border: `1px solid ${colors.gray300}`,
            borderRadius: 8,
            padding: '8px 14px',
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          🖨️ Print this guide
        </button>
      </div>

      <P>
        Putts Differential is simple: fewer putts wins. Every player records his putts on every hole,
        and at the end each pair of players settles the difference in total putts × the wager.
      </P>

      <StepTracker
        storageKey="tto_guide_putts_game_steps"
        steps={['Add Putts Differential', 'Who\u2019s in', 'Play & enter putts', 'Results & settle']}
        note="Steps 1 and 2 happen before the round; 3 and 4 on the day. Tap a step to jump to it."
      />

      <H2>Before you start</H2>
      <UL>
        <LI>
          <strong>Putts Differential needs live scoring.</strong> Every player in it needs his scores entered in
          the app as he plays, on his own phone or by the player keeping his group&rsquo;s card.
        </LI>
        <LI>
          <strong>Putts Differential isn&rsquo;t in your games list?</strong> Games come with the Plus plan and
          above. On Free or Starter, upgrade under Settings &rarr; Subscription &amp; Billing.
        </LI>
        <LI>
          Decide with your group what a putt is worth, and agree on what counts as a putt (for
          example, whether a stroke from the fringe counts).
        </LI>
      </UL>

      <H2>Setup, step by step</H2>

      <Step n={1} title="Add Putts Differential" when="Before the round">
        <UL>
          <LI>
            Click <Btn>🎮 Games</Btn> on the tee time card and tick <strong>Putts Differential</strong>.
          </LI>
          <LI>
            <strong>Wager:</strong> the amount <strong>per putt differential</strong>. $0 shows the
            putt totals and settles no money.
          </LI>
          <LI>
            Click <Btn>Save Games</Btn>.
          </LI>
        </UL>
      </Step>

      <Step n={2} title="Choose who's in" when="After you save, until the first score">
        <UL>
          <LI>
            A new Putts Differential game starts with nobody in it. After you save, the game shows a{' '}
            <strong>Player Opt-Ins</strong> list with every player on the tee time.
          </LI>
          <LI>
            Click a player&rsquo;s <strong>— Out</strong> to make it <strong>✓ In</strong>, or the
            reverse.
          </LI>
          <LI>
            Players can also tap <Btn>Opt In</Btn> in their own app, until the first score of the
            round is entered. After that only you can add or remove a player.
          </LI>
          <LI>
            Only players who are in settle putts with each other.
          </LI>
        </UL>
      </Step>

      <H2>On the day</H2>

      <Step n={3} title="Play and enter your putts" when="On every hole">
        <UL>
          <LI>
            Enter <strong>putts</strong> under the score on every hole, with <strong>−</strong> and{' '}
            <strong>+</strong>. Keeping the card for your group? Each player&rsquo;s row has its own
            putts.
          </LI>
          <LI>
            <strong>Chip-in?</strong> Tap the putts box once so the 0 turns dark. A grey 0 means
            nothing has been entered yet.
          </LI>
          <LI>
            <strong>Every hole needs putts.</strong> A hole left blank isn&rsquo;t counted.
          </LI>
          <LI>
            Players follow the totals in their app: <strong>🏆 Leaderboard</strong> &rarr;{' '}
            <strong>💰 Games</strong>.
          </LI>
        </UL>
      </Step>

      <Step n={4} title="Results and settling up" when="When the last card is in">
        <UL>
          <LI>
            The round finalizes itself once every card is complete. You can also click{' '}
            <Btn>🏁 Finalize Round &amp; Save Settlements</Btn> in <Btn>🎮 Games</Btn>.
          </LI>
          <LI>
            Each pair of players settles the difference in total putts × the wager. Mark 28, Sam 30
            and Kirk 32 at $0.50 a putt: Mark collects $1 from Sam and $2 from Kirk, and Sam collects
            $1 from Kirk.
          </LI>
          <LI>
            With money on the game, TTO settles it player to player, every player gets a statement,
            and you can email everyone their results with one click. At $0 the statement shows the
            results only.
          </LI>
          <LI>
            <strong>Something needs fixing after finalize?</strong> Click <Btn>↶ Reopen Round</Btn>{' '}
            in <Btn>🎮 Games</Btn>, make the change, then finalize again so the money follows.
          </LI>
        </UL>
      </Step>

      <H2>Running Putts Differential in an event?</H2>
      <P>
        Set the event up with its format guide (📖 Guides &rarr; Setting up Events / Tournaments).
        At the step where you add the main game, tick <strong>Putts Differential</strong> beside it and set it
        up as in step 1 above.
      </P>
      <H2>What changes once scoring starts</H2>
      <UL>
        <LI>
          <strong>Players can no longer opt in or out themselves</strong> once the first score of the
          round is entered. You still can, from <Btn>🎮 Games</Btn>.
        </LI>
        <LI>
          <strong>Settings can still change</strong>, but change them only if you must: the results
          and any money follow the new settings.
        </LI>
      </UL>

      <FAQ id="putts-game" faqs={FAQS} />

      <div className="guide-no-print">
        <GuideCTA />
      </div>
    </article>
  );
}
